import { Link } from "react-router-dom";
import { Landmark, Phone, Mail, MapPin, MessageCircle, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { waLink, telLink, track } from "../../lib/site";

export default function Footer() {
  const { settings, products } = useSite();
  const social = settings.social || {};
  return (
    <footer data-testid="site-footer" className="bg-brand-dark text-white">
      <div className="container-site py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="mb-5">
            <img src="/assets/bharatbest-logo.png" alt="BharatBest Fintech Private Limited" className="h-11 w-auto rounded-lg bg-white px-2 py-1" />
          </div>
          <p className="text-sm text-white/60 leading-relaxed">
            BharatBest Fintech helps customers explore suitable financial solutions through a network of lending partners, subject to eligibility and lender approval.
          </p>
          <div className="flex gap-2.5 mt-6">
            {social.facebook && <a href={social.facebook} aria-label="Facebook" data-testid="social-facebook" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-red transition-colors duration-200"><Facebook size={15} /></a>}
            {social.instagram && <a href={social.instagram} aria-label="Instagram" data-testid="social-instagram" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-red transition-colors duration-200"><Instagram size={15} /></a>}
            {social.linkedin && <a href={social.linkedin} aria-label="LinkedIn" data-testid="social-linkedin" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-red transition-colors duration-200"><Linkedin size={15} /></a>}
            {social.twitter && <a href={social.twitter} aria-label="Twitter" data-testid="social-twitter" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-brand-red transition-colors duration-200"><Twitter size={15} /></a>}
          </div>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-brand-gold mb-4">Loan Products</h4>
          <ul className="space-y-2.5 text-sm text-white/70">
            {products.map((p) => (
              <li key={p.id}><Link data-testid={`footer-product-${p.slug}`} className="hover:text-white transition-colors duration-200" to={`/loans/${p.slug}`}>{p.name}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-brand-gold mb-4">Useful Links</h4>
          <ul className="space-y-2.5 text-sm text-white/70">
            <li><Link data-testid="footer-home" className="hover:text-white transition-colors duration-200" to="/">Home</Link></li>
            <li><Link data-testid="footer-about" className="hover:text-white transition-colors duration-200" to="/about">About Us</Link></li>
            <li><Link data-testid="footer-loans" className="hover:text-white transition-colors duration-200" to="/loans">Loan Products</Link></li>
            <li><Link data-testid="footer-emi" className="hover:text-white transition-colors duration-200" to="/calculators/emi">EMI Calculator</Link></li>
            <li><Link data-testid="footer-eligibility" className="hover:text-white transition-colors duration-200" to="/calculators/eligibility">Eligibility</Link></li>
            <li><Link data-testid="footer-blog" className="hover:text-white transition-colors duration-200" to="/blog">Blog</Link></li>
            <li><Link data-testid="footer-contact" className="hover:text-white transition-colors duration-200" to="/contact">Contact</Link></li>
            <li><Link data-testid="footer-apply" className="hover:text-white transition-colors duration-200" to="/apply">Apply Now</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-brand-gold mb-4">Contact Us</h4>
          <p className="text-sm font-bold text-white/90 mb-4">BharatBest Fintech Private Limited</p>
          <ul className="space-y-3.5 text-sm text-white/70">
            <li>
              <a href={telLink(settings)} data-testid="footer-phone" onClick={() => track("phone_click")}
                className="flex items-center gap-2.5 hover:text-white transition-colors duration-200">
                <Phone size={15} className="text-brand-gold shrink-0" /> {settings.phone_display || settings.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${settings.email}`} data-testid="footer-email"
                className="flex items-center gap-2.5 hover:text-white transition-colors duration-200 break-all">
                <Mail size={15} className="text-brand-gold shrink-0" /> {settings.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <MapPin size={15} className="text-brand-gold shrink-0 mt-0.5" />
              <span data-testid="footer-address" className="leading-relaxed">{settings.address}</span>
            </li>
            <li>
              <a href={waLink(settings)} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" onClick={() => track("whatsapp_click")}
                className="inline-flex items-center gap-2 mt-1 rounded-full bg-[#1FA855] px-4 py-2 text-xs font-bold text-white transition-transform duration-200 hover:-translate-y-0.5">
                <MessageCircle size={14} /> Chat with us on WhatsApp
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-site pt-8 pb-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/50">
          <Link data-testid="footer-privacy" className="hover:text-white transition-colors duration-200" to="/privacy-policy">Privacy Policy</Link>
          <Link data-testid="footer-terms" className="hover:text-white transition-colors duration-200" to="/terms">Terms & Conditions</Link>
          <Link data-testid="footer-disclaimer" className="hover:text-white transition-colors duration-200" to="/disclaimer">Disclaimer</Link>
          <Link data-testid="footer-cookie" className="hover:text-white transition-colors duration-200" to="/cookie-policy">Cookie Policy</Link>
          <Link data-testid="footer-consent" className="hover:text-white transition-colors duration-200" to="/consent-policy">Consent Policy</Link>
        </div>
        <div className="container-site pb-8">
          <p data-testid="footer-disclaimer-text" className="text-xs leading-relaxed text-white/40 border-t border-white/10 pt-6">
            {settings.disclaimer}
          </p>
          <p className="text-xs text-white/40 mt-4">© {settings.company_name || "BharatBest Fintech Private Limited"}. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
}
